import { c as _c } from "react/compiler-runtime";
import { Button } from "@repo/ui/components/ui/button";
import { Label } from "@repo/ui/components/ui/label";
import { Switch } from "@repo/ui/components/ui/switch";
import { AlertTriangle, ArrowRightIcon, Check, InfoIcon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { PLAN_BY_ID } from "../auth-flow/signup/constants";
import { BillingInterval, SubscriptionTier } from "../auth-flow/signup/types";
import { cva } from "class-variance-authority";
import { cn } from "@repo/common/utils/cn";
import { motion } from "motion/react";
import { Badge } from "@repo/ui/components/ui/badge";
import { Body } from "@repo/ui/components/ui/text";
import { Card, CardContent } from "@repo/ui/components/ui/card";
import { RichTextRenderer } from "@repo/ui/components/ui/rich-text-renderer";
import { useActivePlans } from "./hooks/use-active-plans";
import uniqBy from "lodash/uniqBy";
import { FeatureTable } from "./feature-table";
import { Permanent_Marker } from "next/font/google";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/ui/components/ui/tooltip";
import { AlertDescription } from "@repo/ui/components/ui/alert";
import { Alert } from "@repo/ui/components/ui/alert";
const permanentMarker = Permanent_Marker({
  weight: "400",
  subsets: ["latin"]
});
const buttonVariants = cva(["w-full h-12"], {
  variants: {
    variant: {
      SOLO: "text-brand-primary-950 hover:bg-surface active:bg-surface",
      GROWTH: "bg-brand-primary-100 text-brand-primary-950 hover:bg-brand-primary-100 active:bg-brand-primary-100",
      ENTERPRISE: "bg-brand-primary-inverse text-brand-primary hover:bg-brand-primary-inverse-hover active:bg-brand-primary-hover active:text-brand-primary-inverse"
    }
  }
});
const cardVariants = cva("relative rounded-lg overflow-hidden flex flex-col gap-4 w-full max-w-md p-6", {
  variants: {
    variant: {
      SOLO: "bg-brand-neutral-50",
      GROWTH: "bg-brand-primary-50",
      ENTERPRISE: "bg-brand-primary text-brand-primary-inverse"
    }
  }
});
type TierItem = {
  YEARLY?: {
    ctaText?: string;
    ctaAction?: "contact-support";
  };
  MONTHLY?: {
    ctaText?: string;
    ctaAction?: "contact-support";
  };
};
export type TierConfig = {
  SOLO: TierItem;
  GROWTH: TierItem;
  ENTERPRISE: TierItem;
};
export function Pricing(t0) {
  const $ = _c(45);
  const {
    currentPlan,
    onSelectPlan,
    title,
    isLoading: t1,
    tierConfig,
    onClose,
    showFeatures: t2,
    showSoloDowngradeMessage: t3
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  const showFeatures = t2 === undefined ? true : t2;
  const showSoloDowngradeMessage = t3 === undefined ? false : t3;
  const [billingInterval, setBillingInterval] = useState("YEARLY");
  const activePlans = useActivePlans();
  let t4;
  if ($[0] !== activePlans) {
    t4 = uniqBy(activePlans, "tier.id")?.map(_temp);
    $[0] = activePlans;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const pricingTiers = t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = value => {
      setBillingInterval(value ? "YEARLY" : "MONTHLY");
    };
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const handleToggle = t5;
  const currentPlanExists = Object.values(currentPlan ?? {}).some(Boolean);
  let t6;
  if ($[3] !== currentPlan?.billingInterval || $[4] !== currentPlan?.tierId || $[5] !== currentPlanExists) {
    t6 = (tierId, billingInterval_0) => {
      if (!currentPlanExists) {
        return false;
      }
      if (currentPlan?.tierId?.toUpperCase() === "SOLO" && tierId.toUpperCase() === "SOLO") {
        return true;
      }
      return currentPlan?.tierId?.toUpperCase() === tierId.toUpperCase() && currentPlan?.billingInterval?.toUpperCase() === billingInterval_0.toUpperCase();
    };
    $[3] = currentPlan?.billingInterval;
    $[4] = currentPlan?.tierId;
    $[5] = currentPlanExists;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const isCurrentPlan = t6;
  let t7;
  if ($[7] !== currentPlan?.billingInterval || $[8] !== currentPlan?.tierId) {
    t7 = (tierId_0, billingInterval_1) => currentPlan?.tierId?.toUpperCase() === tierId_0.toUpperCase() && currentPlan?.billingInterval?.toUpperCase() !== billingInterval_1.toUpperCase();
    $[7] = currentPlan?.billingInterval;
    $[8] = currentPlan?.tierId;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const samePlanDifferentBillingInterval = t7;
  let t8;
  if ($[10] !== currentPlanExists || $[11] !== isCurrentPlan || $[12] !== samePlanDifferentBillingInterval) {
    t8 = t9 => {
      const {
        tierId: tierId_1,
        billingInterval: billingInterval_2,
        ctaText,
        ctaAction
      } = t9;
      if (ctaAction === "contact-support") {
        return "Contact support";
      } else {
        if (isCurrentPlan(tierId_1, billingInterval_2)) {
          return `Stay on ${ctaText}`;
        } else {
          if (samePlanDifferentBillingInterval(tierId_1, billingInterval_2)) {
            return `Change to ${billingInterval_2 === "YEARLY" ? "yearly" : "monthly"}`;
          } else {
            return `${currentPlanExists ? "Switch to" : "Join"} ${ctaText}`;
          }
        }
      }
    };
    $[10] = currentPlanExists;
    $[11] = isCurrentPlan;
    $[12] = samePlanDifferentBillingInterval;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const getCTAText = t8;
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Body className="font-normal text-center tracking-wider">Grow. Without breaking.</Body>;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== title) {
    t10 = <div className="flex flex-col gap-2">{t9}<h1 className="text-4xl max-w-2xl font-bold text-center text-brand-primary-950">{title}</h1></div>;
    $[15] = title;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="flex justify-end gap-1"><Badge variant="tonal" className={cn("w-fit self-center bg-[#ffe6dc] text-brand border-brand uppercase rotate-[-15deg] text-xs border-dashed", permanentMarker.className)}>over 20% off</Badge></div>;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Label>Billed monthly</Label>;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  const t13 = billingInterval === "YEARLY";
  let t14;
  if ($[19] !== t13) {
    t14 = <Switch checked={t13} onCheckedChange={handleToggle} className="data-[state=checked]:bg-brand data-[state=unchecked]:bg-status-neutral-lighten-1" />;
    $[19] = t13;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Label>Billed yearly</Label>;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] !== t14) {
    t16 = <div className="flex flex-col gap-4">{t11}<div className="flex items-center gap-2">{t12}{t14}{t15}</div></div>;
    $[22] = t14;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] !== billingInterval || $[25] !== getCTAText || $[26] !== isCurrentPlan || $[27] !== isLoading || $[28] !== onClose || $[29] !== onSelectPlan || $[30] !== pricingTiers || $[31] !== showSoloDowngradeMessage || $[32] !== tierConfig) {
    t17 = pricingTiers?.map((tier, tierIndex) => {
      const isEnterprise = tier.id === "ENTERPRISE";
      const isSolo = tier.id === "SOLO";
      const resolvedTierConfig = tierConfig?.(tier.id, billingInterval);
      const ctaText_0 = resolvedTierConfig?.[billingInterval]?.ctaText || tier.ctaText;
      const ctaAction_0 = resolvedTierConfig?.[billingInterval]?.ctaAction;
      return <motion.div key={tier.TierName} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: tierIndex * 0.05,
        duration: 0.5,
        ease: "easeOut"
      }} className="w-full max-w-sm h-full"><Card key={tierIndex} className="bg-transparent border-none p-0 shadow-lg h-full"><CardContent className={cn(cardVariants({
            variant: tier.id as "SOLO" | "GROWTH" | "ENTERPRISE"
          }), "h-full")}><div className="flex flex-col items-center gap-4"><div className="flex flex-col items-center gap-0"><h3 className={cn("text-2xl text-center font-bold", isEnterprise ? "text-brand-primary-inverse" : "text-foreground")}>{tier.TierName}</h3><>{tier.monthlyText ? <p className={cn("text-lg", isEnterprise ? "text-brand-primary-inverse" : "text-brand-lighter-1", billingInterval === "YEARLY" ? "line-through" : "")}>{billingInterval === "YEARLY" ? `$${tier.monthlyPrice}` : `${tier.monthlyText}`}</p> : <p className="text-lg text-foreground-muted">Free forever</p>}</></div><div className="flex items-end gap-2 mb-1"><span className={cn("text-3xl font-semibold self-start", isEnterprise ? "text-brand-primary-inverse" : "text-brand-neutral-700")}>$</span><span className={cn("text-6xl font-bold -mt-4", isEnterprise ? "text-brand-primary-inverse" : "text-foreground")}>{billingInterval === "YEARLY" ? tier.annualPrice : tier.monthlyPrice}</span><span className={cn("text-3xl font-semibold self-start", isEnterprise ? "text-brand-primary-inverse" : "text-brand-neutral-700")}>/mo</span></div></div><div className={cn("leading-relaxed tracking-wide font-normal", isEnterprise ? "text-brand-primary-inverse" : "text-brand-primary-950")}><RichTextRenderer text={tier.description as any} /></div><div className="flex flex-col gap-10"><div className="flex flex-col gap-2">{tier.feature_items.map(feature_0 => feature_0.list?.map((listItem, listIndex) => <div key={listIndex} className="flex items-center gap-2"><Check className={cn("flex-shrink-0 mt-0.5", isEnterprise ? "text-brand-primary-inverse" : "text-brand")} size={14} strokeWidth={2.5} /><span className={cn("leading-relaxed text-brand-primary-950 font-semibold flex items-center gap-1", isEnterprise ? "text-brand-primary-inverse" : "text-brand-primary-950")}><p>{listItem as any}</p>{listItem === "2-way texting and bulk SMS" && <Tooltip><TooltipTrigger asChild={true}><InfoIcon className="w-4 h-4" /></TooltipTrigger><TooltipContent><p>SMS availability is only guaranteed in the U.S. Canada, and Australia.</p></TooltipContent></Tooltip>}</span></div>))}</div>{isSolo && showSoloDowngradeMessage && <Alert variant="warn"><AlertTriangle className="h-4 w-4" /><AlertDescription className="text-sm font-medium">Selecting Solo may remove access to features you currently have.</AlertDescription></Alert>}<Button onClick={() => {
                if (isCurrentPlan(tier.id, billingInterval)) {
                  onClose?.();
                } else {
                  if (ctaAction_0 === "contact-support") {
                    window.location.href = "mailto:support@copilotcrm.com";
                  } else {
                    onSelectPlan(tier.id, billingInterval);
                  }
                }
              }} className={buttonVariants({
                variant: tier.id as "SOLO" | "GROWTH" | "ENTERPRISE"
              })} disabled={isLoading} variant={tier.id === "SOLO" ? "ghost" : "fill"}>{getCTAText({
                  tierId: tier.id,
                  billingInterval,
                  ctaText: ctaText_0,
                  ctaAction: ctaAction_0
                })}</Button></div></CardContent></Card></motion.div>;
    });
    $[24] = billingInterval;
    $[25] = getCTAText;
    $[26] = isCurrentPlan;
    $[27] = isLoading;
    $[28] = onClose;
    $[29] = onSelectPlan;
    $[30] = pricingTiers;
    $[31] = showSoloDowngradeMessage;
    $[32] = tierConfig;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== t17) {
    t18 = <div className="flex flex-col md:flex-row items-center md:items-start gap-8 justify-center">{t17}</div>;
    $[34] = t17;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <br />;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Body className="font-normal text-center text-sm my-4">All prices in USD. Subscription prices exclude sales tax where applicable. {t19} Annual options are billed annually.{" "}<a href="https://www.home.works/terms-of-service#TermandTermination" target="_blank" rel="noopener noreferrer" className="underline hover:text-brand-primary-950 active:text-brand-primary-950 hover:underline">Refund policy</a>.</Body>;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== showFeatures) {
    t21 = showFeatures && <><FeatureTable /><a href="https://www.home.works/pricing" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-brand-primary-950 hover:text-brand-primary-950 active:text-brand-primary-950 hover:underline">Explore all features<ArrowRightIcon className="w-4 h-4" /></a></>;
    $[38] = showFeatures;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== t10 || $[41] !== t16 || $[42] !== t18 || $[43] !== t21) {
    t22 = <div className="flex flex-col gap-8 items-center justify-center max-w-[1250px] mx-auto pb-8">{t10}{t16}{t18}{t20}{t21}</div>;
    $[40] = t10;
    $[41] = t16;
    $[42] = t18;
    $[43] = t21;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  return t22;
}
function _temp(plan) {
  return {
    ...plan,
    id: plan.tier.id,
    ctaText: PLAN_BY_ID[plan.tier.id].ctaText,
    TierName: PLAN_BY_ID[plan.tier.id].name,
    description: PLAN_BY_ID[plan.tier.id].description,
    annualPrice: PLAN_BY_ID[plan.tier.id].annualPrice,
    monthlyPrice: PLAN_BY_ID[plan.tier.id].monthlyPrice,
    monthlyText: PLAN_BY_ID[plan.tier.id].monthlyText,
    feature_items: PLAN_BY_ID[plan.tier.id].features.map(feature => ({
      list: [feature],
      tier: plan.tier.id
    }))
  };
}